// Location tree from the sensors' location labels (decision H6, Location
// Label Set): country › city › area › site › floor › zone, values are slugs.
// Same node shape and helpers as the static demo's data/locations.js, so the
// demo's screens (path bar, tree, map, drill-down) work on real data.
// Node id = the label path, e.g. "CH/lugano/centro/lugano-via-nassa".
export const LEVELS = ["country", "city", "area", "site", "floor", "zone"] as const;
export type Level = (typeof LEVELS)[number];
export type St = "crit" | "alarm" | "warn" | "delayed" | "ok";

export interface Point {
  /** device UUID */
  id: string;
  serial: string;
  name: string;
  regime: string;
  /** placed at a cabinet or in a room / area of the store (from the installation record) */
  kind?: "cabinet" | "room";
  humidity?: number | null;
  temp: number | null;
  battery: number | null;
  /** LoRaWAN radio of the last uplink (best gateway): RSSI dBm, SNR dB, gateway id */
  rssi?: number | null;
  snr?: number | null;
  gateway?: string | null;
  lastContact: string | null;
  st: St;
  labels: Record<string, unknown>;
  /** owning account (deepest account whose device list holds it) */
  owner?: string | null;
  /** start of the current installation (cabinet data starts here) */
  installedAt?: string | null;
}

export interface LocNode {
  id: string;
  level: Level;
  value: string;
  name: string;
  parent: LocNode | null;
  children: LocNode[];
  map?: { x: number; y: number };
  /** sites: the measuring points (sensors) in it */
  points: Point[];
  /** sites: cabinets defined without a sensor yet */
  empty?: { id: string; name: string; type: string; regime: string }[];
  state?: { st: St };
}

// Positions on the demo's 640 × 400 Switzerland drawing (static demo
// data/locations.js CITIES, plus Winterthur), by city label value.
export const CITY_POS: Record<string, { x: number; y: number; name: string }> = {
  zurich: { x: 384, y: 98, name: "Zürich" }, basel: { x: 258, y: 88, name: "Basel" }, "st-gallen": { x: 474, y: 96, name: "St. Gallen" },
  luzern: { x: 318, y: 150, name: "Luzern" }, bern: { x: 210, y: 190, name: "Bern" }, lausanne: { x: 112, y: 262, name: "Lausanne" },
  geneve: { x: 58, y: 304, name: "Genève" }, sion: { x: 206, y: 300, name: "Sion" }, chur: { x: 520, y: 190, name: "Chur" },
  bellinzona: { x: 414, y: 282, name: "Bellinzona" }, lugano: { x: 404, y: 330, name: "Lugano" }, chiasso: { x: 398, y: 370, name: "Chiasso" },
  winterthur: { x: 400, y: 86, name: "Winterthur" },
};

const RANK: Record<St, number> = { crit: 4, alarm: 3, warn: 2, delayed: 1, ok: 0 };
export const worse = (a: St, b: St): St => (RANK[a] >= RANK[b] ? a : b);

const pretty = (v: string) => v.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Build the tree. names[key][value] = display name (label value catalogue). */
export function buildTree(points: Point[], names: Record<string, Record<string, string>>, stores: { value: string; city: string; area: string }[] = [], cabinets: { id: string; site: string; name: string; type: string; regime: string }[] = []): LocNode {
  const nm = (key: string, v: string) => names[key]?.[v] || (key === "city" && CITY_POS[v]?.name) || (key === "country" && v === "CH" ? "Switzerland" : pretty(v));
  const mk = (id: string, level: Level, value: string, parent: LocNode | null): LocNode => ({ id, level, value, name: nm(level, value), parent, children: [], points: [] });
  const country = (p: Point) => String(p.labels.country || "CH");
  const roots = new Map<string, LocNode>();
  for (const p of points) {
    const c = country(p);
    if (!roots.has(c)) roots.set(c, mk(c, "country", c, null));
    let n = roots.get(c)!;
    for (const lv of ["city", "area", "site"] as const) {
      const v = p.labels[lv];
      if (!v) break;
      const id = `${n.id}/${String(v)}`;
      let ch = n.children.find((x) => x.id === id);
      if (!ch) {
        ch = mk(id, lv, String(v), n);
        if (lv === "city" && CITY_POS[String(v)]) ch.map = { x: CITY_POS[String(v)].x, y: CITY_POS[String(v)].y };
        n.children.push(ch);
      }
      n = ch;
    }
    if (n.level === "site") n.points.push(p);
  }
  // stores from the catalogue, also those without sensors yet
  for (const st of stores) {
    if (!roots.has("CH")) roots.set("CH", mk("CH", "country", "CH", null));
    let n = roots.get("CH")!;
    for (const [lv, v] of [["city", st.city], ["area", st.area], ["site", st.value]] as const) {
      if (!v) break;
      const id = `${n.id}/${v}`;
      let ch = n.children.find((x) => x.id === id);
      if (!ch) { ch = mk(id, lv, v, n); if (lv === "city" && CITY_POS[v]) ch.map = { x: CITY_POS[v].x, y: CITY_POS[v].y }; n.children.push(ch); }
      n = ch;
    }
  }
  const root = roots.get("CH") || [...roots.values()][0] || mk("CH", "country", "CH", null);
  for (const s of sites(root)) {
    s.state = { st: s.points.reduce<St>((w, p) => worse(w, p.st), "ok") };
    const taken = new Set(s.points.map((p) => p.name.toLowerCase()));
    s.empty = cabinets.filter((c) => c.site === s.value && !taken.has(c.name.toLowerCase()));
  }
  const sort = (n: LocNode) => { n.children.sort((a, b) => a.name.localeCompare(b.name)); n.children.forEach(sort); };
  sort(root);
  return root;
}

export function find(root: LocNode, id: string): LocNode | null {
  if (root.id === id) return root;
  for (const c of root.children) if (id === c.id || id.startsWith(c.id + "/")) { const f = find(c, id); if (f) return f; }
  return null;
}
export const pathOf = (n: LocNode) => { const out: LocNode[] = []; for (let x: LocNode | null = n; x; x = x.parent) out.unshift(x); return out; };
export function sites(n: LocNode, out: LocNode[] = []): LocNode[] { if (n.level === "site") out.push(n); else n.children.forEach((c) => sites(c, out)); return out; }
export function rollup(n: LocNode) {
  const s = sites(n);
  const r = { sites: s.length, crit: 0, alarm: 0, warn: 0, delayed: 0, ok: 0, worst: "ok" as St, cabinets: 0 };
  s.forEach((x) => { const st = x.state?.st || "ok"; r[st]++; r.cabinets += x.points.filter((p) => p.kind !== "room").length + (x.empty?.length || 0); r.worst = worse(r.worst, st); });
  return r;
}
// The account header already limits the data to the caller's accounts, so
// every node the tree holds is visible ("full"); kept for the demo's API.
export const inScope = (_n: LocNode, _scope?: unknown) => "full" as const;
export const visibleChildren = (n: LocNode, _scope?: unknown) => n.children;
